import React, { useState, useEffect, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import { CircularProgress } from "@mui/material";
import { Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

// My Files
import { DatabaseContext, UserContext } from "../../context";
import AvatarUploadButton from "../management/AvatarUploadButton";
import ToolbarWithLogo from "../ToolbarWithLogo";
import firebase from "../../firebase";
import ChangePasswordDialog from "./ChangePasswordDialog";
import ChangeEmailDialog from "./ChangeEmailDialog";
import { ColorModeContext } from "../../colorMode";

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

export default function MyAccountPage() {
    const history = useHistory();
    const location = useLocation();

    const { currentUserData } = useContext(UserContext);
    const { mode: colorMode, setMode: setColorMode } =
        useContext(ColorModeContext);

    const [hasChangedAvatar, setHasChangedAvatar] = useState(false);

    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phone, setPhone] = useState("");
    const [allowEmailNotifications, setAllowEmailNotifications] =
        useState(true);

    const [changePasswordDialogIsOpen, setChangePasswordDialogIsOpen] =
        useState(false);

    const [changeEmailDialogIsOpen, setChangeEmailDialogIsOpen] =
        useState(false);

    const [statusMessage, setStatusMessage] = useState("");
    const [statusType, setStatusType] = useState("");
    const [statusMessageIsVisible, setStatusMessageIsVisible] = useState(false);
    const [isInProgress, setIsInProgress] = useState(false);

    const { comments, conversations } = useContext(DatabaseContext);

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            // detaching the listener
            if (!user) {
                // If no user is logged in, redirect to sign in page
                // history.replace("/signin");
                history.replace("/signin?url=" + location.pathname);
            }
        });

        if (currentUserData) {
            setEmail(currentUserData.email);
            setFirstName(currentUserData.firstName);
            setLastName(currentUserData.lastName);
            setPhone(currentUserData.phone);
            setAllowEmailNotifications(
                currentUserData.allowEmailNotifications ?? true,
            );
        }

        return () => unsubscribe();
        // eslint-disable-next-line
    }, [currentUserData, history]);

    function handleChange(event) {
        let newValue = event.target.value;

        if (event.target.id === "firstName") {
            setFirstName(newValue);
        } else if (event.target.id === "lastName") {
            setLastName(newValue);
        } else if (event.target.id === "phone") {
            setPhone(newValue);
        }
    }

    // phone and allowEmailNotifications are personal data and live only on
    // /userprofiles; the name and avatar are not, and stay on /users.
    function buildSaveUpdates(key, imageURLToWrite) {
        const updates = {
            [`users/${key}/firstName`]: firstName,
            [`users/${key}/lastName`]: lastName,
            [`userprofiles/${key}/phone`]: phone,
            [`userprofiles/${key}/allowEmailNotifications`]:
                allowEmailNotifications,
        };

        if (imageURLToWrite !== undefined) {
            updates[`users/${key}/imageURL`] = imageURLToWrite;
        }

        return updates;
    }

    async function handleSaveClick() {
        if (!currentUserData) {
            return;
        }

        setIsInProgress(true);
        let key = currentUserData.key;
        if (hasChangedAvatar) {
            await uploadAvatar(key).then((downloadURL) => {
                // Edit user object in database

                let oldFullName =
                    currentUserData.firstName + " " + currentUserData.lastName;

                firebase
                    .database()
                    .ref()
                    .update(buildSaveUpdates(key, downloadURL))
                    .catch((error) => {
                        let errorCode = error.code;
                        let errorMessage = error.message;
                        console.log(errorCode + ":" + errorMessage);
                        setIsInProgress(false);
                        setStatusMessage(error.message);
                        setStatusType("error");
                        setStatusMessageIsVisible(true);
                    });

                if (oldFullName !== firstName + " " + lastName) {
                    changeNameInCommentsAndConversations();
                }

                setIsInProgress(false);
                history.goBack();
            });
        } else {
            // Edit user object in database

            let oldFullName =
                currentUserData.firstName + " " + currentUserData.lastName;

            firebase
                .database()
                .ref()
                .update(buildSaveUpdates(key))
                .catch((error) => {
                    let errorCode = error.code;
                    let errorMessage = error.message;
                    console.log(errorCode + ":" + errorMessage);
                    setIsInProgress(false);
                    setStatusMessage(error.message);
                    setStatusType("error");
                    setStatusMessageIsVisible(true);
                });

            if (oldFullName !== firstName + " " + lastName) {
                changeNameInCommentsAndConversations();
            }

            setIsInProgress(false);
            history.goBack();
        }
        history.goBack();
    }

    // If the user name was changed, go through thier comments and conversations and change the names there as well.
    function changeNameInCommentsAndConversations() {
        let fullName = firstName + " " + lastName;
        let key = currentUserData.key;

        // Update Comments
        comments.forEach((comment) => {
            if (comment.user === key) {
                let ref = firebase
                    .database()
                    .ref("comments")
                    .child(comment.key);

                ref.update({ author: fullName });
            }
        });

        // Update Conversations
        conversations.forEach((conv) => {
            conv.messages.forEach((message) => {
                if (message.user === key) {
                    let ref = firebase
                        .database()
                        .ref("conversations")
                        .child(conv.key)
                        .child("messages")
                        .child(message.key);
                    ref.update({ fullName: fullName });
                }
            });
        });
    }

    function didChangeAvatar() {
        setHasChangedAvatar(true);
    }

    function handleCloseChangePasswordDialog() {
        setChangePasswordDialogIsOpen(false);
    }

    function handleCloseChangeEmailDialog() {
        setChangeEmailDialogIsOpen(false);
    }

    function showPasswordChangeStatus() {
        handleCloseChangePasswordDialog();
        setStatusType("success");
        setStatusMessage("Password Updated");
        setStatusMessageIsVisible(true);
    }

    function showEmailChangeStatus() {
        handleCloseChangeEmailDialog();
        setStatusType("success");
        setStatusMessage("Email Updated");
        setStatusMessageIsVisible(true);
    }

    function handlePressUpdateEmailButton() {
        setChangeEmailDialogIsOpen(true);
    }
    function handlePressUpdatePasswordButton() {
        setChangePasswordDialogIsOpen(true);
    }

    const handleAlertClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setStatusMessageIsVisible(false);
    };

    function uploadAvatar(userKey) {
        return new Promise((resolve, reject) => {
            let storage = firebase.storage();

            currentUserData.avatarEditor
                .getImageScaledToCanvas()
                .toBlob(function (blob) {
                    // Upload the new file
                    let avatarRef = storage
                        .ref()
                        .child("avatars/users/" + userKey + "/avatar.png");

                    avatarRef
                        .put(blob)
                        .then((snapshot) => {
                            // Upload was successful
                            console.log("Upload Successful");
                            currentUserData.avatar = null;
                            avatarRef.getDownloadURL().then((url) => {
                                resolve(url);
                            });
                        })
                        .catch((error) => {
                            let errorCode = error.code;
                            let errorMessage = error.message;
                            console.log(errorCode + ":" + errorMessage);
                            setIsInProgress(false);
                            setStatusMessage(error.message);
                            setStatusType("error");
                            setStatusMessageIsVisible(true);
                            reject();
                        });
                });
        });
    }

    return (
        <>
            <Box
                sx={{
                    minHeight: "calc(100vh - 64px)",
                    backgroundColor: "background.default",
                    pt: { xs: 2, sm: 3, md: 4 },
                }}
            >
                <Container maxWidth="md" sx={{ px: { xs: 1.5, sm: 3 } }}>
                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 3,
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            boxShadow: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "0 8px 28px rgba(0, 0, 0, 0.28)"
                                    : "0 8px 28px rgba(20, 35, 25, 0.075)",
                        }}
                    >
                        <CardContent
                            sx={{
                                p: { xs: 2.5, sm: 4 },
                                "&:last-child": {
                                    pb: { xs: 2.5, sm: 4 },
                                },
                            }}
                        >
                            <Box sx={{ textAlign: "center", mb: 2 }}>
                                <Typography
                                    variant="h4"
                                    component="h1"
                                    sx={{ mb: 0.75 }}
                                >
                                    My account
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "text.secondary" }}
                                >
                                    Update your profile and account settings.
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    mb: 2.5,
                                }}
                            >
                                <AvatarUploadButton
                                    recipient={currentUserData}
                                    didChangeAvatar={didChangeAvatar}
                                    path="users"
                                    centered
                                    compact
                                />
                            </Box>

                            <Divider sx={{ mb: 2.5 }} />

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 2,
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    backgroundColor: "background.subtle",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        minWidth: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 38,
                                            height: 38,
                                            borderRadius: "50%",
                                            display: "grid",
                                            placeItems: "center",
                                            flexShrink: 0,
                                            color: "primary.main",
                                            backgroundColor: "action.hover",
                                        }}
                                    >
                                        {colorMode === "dark" ? (
                                            <DarkModeOutlinedIcon />
                                        ) : (
                                            <LightModeOutlinedIcon />
                                        )}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 700 }}
                                        >
                                            Dark mode
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            component="p"
                                            sx={{ color: "text.secondary" }}
                                        >
                                            Use a darker appearance on this
                                            device.
                                        </Typography>
                                    </Box>
                                </Box>
                                <Switch
                                    checked={colorMode === "dark"}
                                    onChange={(event) =>
                                        setColorMode(
                                            event.target.checked
                                                ? "dark"
                                                : "light",
                                        )
                                    }
                                    color="primary"
                                    inputProps={{
                                        "aria-label": "Use dark mode",
                                    }}
                                />
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "1fr 1fr",
                                    },
                                    gap: 2,
                                }}
                            >
                                <TextField
                                    id="firstName"
                                    label="First Name"
                                    variant="outlined"
                                    value={firstName}
                                    onChange={handleChange}
                                />
                                <TextField
                                    id="lastName"
                                    label="Last Name"
                                    variant="outlined"
                                    value={lastName}
                                    onChange={handleChange}
                                />
                                <TextField
                                    id="phone"
                                    label="Mobile Number"
                                    variant="outlined"
                                    value={phone}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={{ gridColumn: "1 / -1" }}
                                />
                                <TextField
                                    id="email"
                                    label="E-mail"
                                    variant="outlined"
                                    value={email}
                                    onChange={handleChange}
                                    fullWidth
                                    disabled
                                    sx={{ gridColumn: "1 / -1" }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    mt: 2.5,
                                    p: 2,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    backgroundColor: "background.subtle",
                                }}
                            >
                                <FormControlLabel
                                    sx={{ m: 0 }}
                                    control={
                                        <Switch
                                            checked={allowEmailNotifications}
                                            onChange={(e) =>
                                                setAllowEmailNotifications(
                                                    e.target.checked,
                                                )
                                            }
                                            color="primary"
                                        />
                                    }
                                    label="Allow Email Notifications"
                                />
                                <Typography
                                    variant="caption"
                                    component="p"
                                    sx={{
                                        color: "text.secondary",
                                        ml: 6,
                                        mt: -0.5,
                                    }}
                                >
                                    Receive email updates when new content is
                                    shared with you.
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "1fr 1fr",
                                    },
                                    gap: 1.5,
                                    mt: 2.5,
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<EmailOutlinedIcon />}
                                    onClick={handlePressUpdateEmailButton}
                                    sx={{ py: 1.25 }}
                                >
                                    Update Email
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<LockOutlinedIcon />}
                                    onClick={handlePressUpdatePasswordButton}
                                    sx={{ py: 1.25 }}
                                >
                                    Update Password
                                </Button>
                            </Box>

                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                // The form is blank until currentUserData
                                // arrives; saving it would write blank names
                                // over the real ones.
                                disabled={isInProgress || !currentUserData}
                                onClick={handleSaveClick}
                                startIcon={
                                    isInProgress ? (
                                        <CircularProgress
                                            size={18}
                                            color="inherit"
                                        />
                                    ) : (
                                        <SaveOutlinedIcon />
                                    )
                                }
                                sx={{
                                    mt: 3,
                                    py: 1.35,
                                    fontWeight: 700,
                                }}
                            >
                                {isInProgress
                                    ? "Saving changes"
                                    : "Save changes"}
                            </Button>
                        </CardContent>
                    </Card>
                </Container>
                <ToolbarWithLogo />
            </Box>
            <Snackbar
                open={statusMessageIsVisible}
                autoHideDuration={6000}
                onClose={handleAlertClose}
            >
                <Alert onClose={handleAlertClose} severity={statusType}>
                    {statusMessage}
                </Alert>
            </Snackbar>
            <ChangePasswordDialog
                open={changePasswordDialogIsOpen}
                onClose={handleCloseChangePasswordDialog}
                onSuccess={showPasswordChangeStatus}
            />
            <ChangeEmailDialog
                open={changeEmailDialogIsOpen}
                onClose={handleCloseChangeEmailDialog}
                onSuccess={showEmailChangeStatus}
            />
        </>
    );
}
